import { NET_PROBE_URL } from '../../env.json';

/*
 * Measured line speed — the Speedtest kind.
 *
 * Two directions, and they are not interchangeable. measureDownlink answers "what can this
 * connection fetch", which is a student's question. measureUplink answers "what can this
 * connection send", which is the publisher's — and on the asymmetric lines most people are
 * actually on (DSL, 4G, home cable) the second is a fraction of the first. Handing a
 * download figure to the encoder is what made a teacher on good wifi ramp into an uplink
 * that was never there, lose packets, and be told their network was poor.
 *
 * Nothing in WebRTC's own statistics can produce a figure like "42 Mbps". The congestion
 * controller's availableOutgoingBitrate only ever probes a little above what the call is
 * already sending, so on a class streaming 1.5 Mbps it reports somewhere near 2, however
 * fat the line underneath actually is. The only way to learn a connection can carry 42 is
 * to try to fill it.
 *
 * Which is also the reason this does not run continuously. A test competes with the class
 * for the exact bandwidth it is measuring, and on a student's mobile plan it spends their
 * data to tell them how much data they have. So the caller runs one proper test on join, a
 * short one whenever the live stats say the line has changed, and holds the last figure on
 * screen in between.
 */

// Cloudflare's public measurement endpoint: CORS-open, serves an arbitrary byte count, and
// needs nothing built server-side. Swap this one constant for an endpoint on your own
// infrastructure to measure the path that actually carries the class — the shape of the
// request (GET, ?bytes=N appended) is all this module depends on.
export const SPEED_TEST_SOURCE = 'https://speed.cloudflare.com/__down?bytes=';

// Where the upload probe posts. Per environment via env.json, because unlike the download
// this one has to land on our own media origin: the number it produces sets what a teacher
// is allowed to publish, and the only path worth measuring is the path the class takes.
export const UPLOAD_TEST_URL = NET_PROBE_URL || 'https://broadcast.aulas.in/netprobe';

// TCP slow start means a fast line spends its first moments looking slow. That opening
// stretch is timed but not counted, so the figure reflects the connection once it has
// actually opened up rather than the ramp.
const RAMP_MS = 250;

// Covers what a read loop cannot see: DNS, connect, TLS, and a server that accepts the
// request and then goes quiet.
const CONNECT_GRACE_MS = 5000;

/**
 * Download until either budget runs out, then report the throughput observed.
 *
 * Both limits matter. budgetMs bounds how long the class waits; maxBytes bounds what the
 * measurement costs on a fast line, where three seconds of reading would pull down 15MB to
 * establish something two seconds already established.
 *
 * Resolves to {bps, bytes, at} or null — null covers every failure (offline, blocked,
 * aborted, endpoint down), because a speed readout that cannot measure should simply keep
 * showing the last figure it had rather than surface a network error into a live class.
 */
export async function measureDownlink({budgetMs = 3000, maxBytes = 12000000, signal} = {}) {
    const controller = new AbortController();
    const abort = () => controller.abort();
    signal?.addEventListener('abort', abort);
    const guard = setTimeout(abort, budgetMs + CONNECT_GRACE_MS);
    try {
        const requestedAt = performance.now();
        const response = await fetch(`${SPEED_TEST_SOURCE}${maxBytes}&_=${Date.now()}`, {
            cache: 'no-store',
            signal: controller.signal,
        });
        if (!response.ok || !response.body) return null;

        const reader = response.body.getReader();
        let bytes = 0;
        let firstAt = 0;
        let lastAt = 0;
        let rampAt = 0;
        let rampBytes = 0;
        for (;;) {
            const {done, value} = await reader.read();
            if (done) break;
            lastAt = performance.now();
            if (!firstAt) firstAt = lastAt;
            bytes += value.byteLength;
            if (!rampAt && lastAt - firstAt >= RAMP_MS) {
                rampAt = lastAt;
                rampBytes = bytes;
            }
            if (lastAt - firstAt >= budgetMs || bytes >= maxBytes) break;
        }
        // Hang up rather than let the remainder of the payload arrive unmeasured, still
        // costing the class bandwidth.
        reader.cancel().catch(() => {});
        if (!bytes) return null;

        const from = rampAt || firstAt;
        const counted = rampAt ? bytes - rampBytes : bytes;
        const seconds = (lastAt - from) / 1000;
        if (seconds > 0 && counted > 0) {
            return {bps: (counted * 8) / seconds, bytes, at: Date.now()};
        }
        // A transfer that arrived in one or two chunks leaves no window to divide by. Timing
        // the whole request instead folds in latency and understates the line, but a low
        // reading beats no reading, and this only happens on transfers too small to have
        // been worth much precision anyway.
        const wholeSeconds = (lastAt - requestedAt) / 1000;
        return wholeSeconds > 0 ? {bps: (bytes * 8) / wholeSeconds, bytes, at: Date.now()} : null;
    } catch (error) {
        return null;
    } finally {
        clearTimeout(guard);
        signal?.removeEventListener('abort', abort);
    }
}

// The send side ramps too, and for one more reason than the read side did: the browser hands
// the socket more than the line can carry, so the first progress events time a buffer
// filling rather than a line carrying. Slightly longer than RAMP_MS for that reason.
const UPLOAD_RAMP_MS = 300;

/*
 * A body that cannot be compressed away.
 *
 * Zero-filled bytes cost a gzip-enabled proxy almost nothing, and three megabytes that
 * arrive as a few hundred report a line a hundred times faster than it is — the exact error
 * this whole change exists to remove. crypto.getRandomValues fills at most 65536 bytes per
 * call, so the buffer is walked in chunks.
 */
const incompressibleBody = (bytes) => {
    const buffer = new Uint8Array(bytes);
    const chunk = 65536;
    for (let offset = 0; offset < bytes; offset += chunk) {
        crypto.getRandomValues(buffer.subarray(offset, Math.min(offset + chunk, bytes)));
    }
    return new Blob([buffer], {type: 'application/octet-stream'});
};

/**
 * Upload the same way measureDownlink downloads, and report the throughput observed.
 *
 * XMLHttpRequest rather than fetch, which is not a preference: fetch has no upload progress
 * event at all, so the only figure it can produce is total bytes over total time — latency,
 * server processing and all folded in. xhr.upload.onprogress is the only way in a browser to
 * watch a body leave.
 *
 * Resolves to {bps, bytes, at} or null — the same shape measureDownlink returns, so
 * rungForSpeed and codecOptionsForRung need to know nothing about which direction was
 * measured. null covers the failures that taught us nothing (endpoint down, CORS, offline).
 */
export function measureUplink({bytes = 3000000, timeoutMs = 4000, signal} = {}) {
    return new Promise((resolve) => {
        let request;
        try {
            request = new XMLHttpRequest();
        } catch (error) {
            resolve(null);
            return
        }

        let guard = null;
        let settled = false;
        const startedAt = performance.now();
        let firstAt = 0;
        let lastAt = 0;
        let lastLoaded = 0;
        let rampAt = 0;
        let rampLoaded = 0;

        const abort = () => { try { request.abort() } catch (error) {} };
        const finish = (value) => {
            if (settled) return
            settled = true;
            clearTimeout(guard);
            signal?.removeEventListener('abort', abort);
            resolve(value);
        };

        // What the samples collected so far say, or null when they say nothing.
        const reading = () => {
            const counted = rampAt ? lastLoaded - rampLoaded : lastLoaded;
            const seconds = (lastAt - (rampAt || firstAt)) / 1000;
            if (seconds > 0 && counted > 0) {
                return {bps: (counted * 8) / seconds, bytes: lastLoaded, at: Date.now()};
            }
            // Everything the browser had went into the socket buffer in a single event,
            // leaving no window to divide by — the same case measureDownlink covers by
            // timing the whole request. It folds in latency and understates the line, which
            // is the safe direction to be wrong in for a figure that caps an encoder.
            const wholeSeconds = (performance.now() - startedAt) / 1000;
            return lastLoaded > 0 && wholeSeconds > 0
                ? {bps: (lastLoaded * 8) / wholeSeconds, bytes: lastLoaded, at: Date.now()}
                : null
        };

        // Resolving rather than rejecting: a probe that ran out of time still watched the
        // line for the whole of that time, which is a longer look than the recheck budget
        // gets on purpose. Only a probe that saw nothing at all reports null.
        guard = setTimeout(() => { abort(); finish(reading()) }, timeoutMs);
        signal?.addEventListener('abort', abort);

        request.upload.onprogress = (event) => {
            lastAt = performance.now();
            lastLoaded = event.loaded;
            if (!firstAt) firstAt = lastAt;
            if (!rampAt && lastAt - firstAt >= UPLOAD_RAMP_MS) {
                rampAt = lastAt;
                rampLoaded = event.loaded;
            }
        };
        request.onload = () => finish(
            request.status >= 200 && request.status < 300 ? reading() : null);
        request.onerror = () => finish(null);
        request.onabort = () => finish(reading());

        try {
            request.open('POST', `${UPLOAD_TEST_URL}?bytes=${bytes}&_=${Date.now()}`);
            request.setRequestHeader('Content-Type', 'application/octet-stream');
            request.send(incompressibleBody(bytes));
        } catch (error) {
            finish(null);
        }
    });
}
